import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from 'src/app/models/user';
import { GumUsersService } from 'src/app/services/gumservices/gum-users.service';
import { GumOrganizationsService } from 'src/app/services/gumservices/gum-organizations.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { forkJoin, from, Observable, of, Subscription } from 'rxjs';
import { switchMap, defaultIfEmpty, concatMap } from 'rxjs/operators';
import { Property } from 'src/app/models/property';
import { Acl } from 'src/app/models/acl';
import { Organization } from 'src/app/models/organization.model';
import { EnumOrganizationAccessLevels } from 'src/app/models/enum-organization-access-levels';
import { EnumUserAccessLevels } from 'src/app/models/enum-user-access-levels';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { EnumAffinities } from 'src/app/models/gum-models/enum-affinities';
import { ExternalsParametersService } from 'src/app/services/external-parameters.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ExternalResponse } from 'src/app/models/external-response';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GumPropertiesComponent } from '../gum-properties/gum-properties.component';
import { GumRightsComponent } from '../gum-rights/gum-rights.component';
import { ProfilService } from 'src/app/services/profil.service';
import { ProfilModel } from 'src/app/models/profil.model';
import { MatRadioModule } from '@angular/material/radio';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatCardModule,
    MatTabGroup,
    MatTab,
    MatIconModule,
    MatTooltipModule,
    GumPropertiesComponent,
    //GumRightsComponent,
    MatRadioModule,
    MatAutocompleteModule,
    NgxSpinnerModule,
  ],
})
export class UserFormComponent implements OnInit, OnDestroy {
  @Input() forceProfil: string | null = null;
  @Input() readOnlyProfilSelection: boolean = false;
  @Input() adminMode: boolean = false;

  private spinnerService = inject(NgxSpinnerService);

  isAdminMode = false;
  canEdit = false;

  userForm!: UntypedFormGroup;
  userInformations!: User;
  userProperties: Property[] = [];
  userRights: Acl[] = [];
  isCreation!: boolean;
  organizationList!: Organization[];
  currentUser!: User;
  propertiesValid!: boolean;
  affinity = EnumAffinities.User;
  organizationAccessLevels = Object.keys(EnumOrganizationAccessLevels);
  userAccessLevels = Object.keys(EnumUserAccessLevels);

  isExternal = false;
  externalParameter!: ExternalParameters;
  externalParametersChange!: Subscription;

  profils: ProfilModel[] = [];
  profilIdToApply: number | null = null;
  selectedProfilProperties: { name: string; value: number }[] = [];

  clientsList: Organization[] = [];

  partenaireInput: string = '';
  clientInput: string = '';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private usersService: GumUsersService,
    private organizationsService: GumOrganizationsService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private externalsParametersService: ExternalsParametersService,
    private profilService: ProfilService
  ) {}

  ngOnInit(): void {
    // Détermine si la fiche est un admin (AdminFormComponent)
    this.isAdminMode = this.adminMode === true;

    // Vérifie les droits du user connecté
    this.usersService.getSessionCurrentUser().subscribe((user) => {
      if (user) {
        this.currentUser = user;

        // ici on ne change PAS isAdminMode, on détermine juste si on peut éditer
        this.canEdit = user.properties?.some(
          (p) => p.name?.trim() === 'NexusAdministrateur' && p.value === '1'
        );
      } else {
        this.toastr.error("Impossible de récupérer l'utilisateur courant.");
        this.router.navigate([this.isAdminMode ? '/admin' : '/users']);
      }
    });

    this.route.queryParams.subscribe((params) => {
      const userId = params['id'];
      const stateUser = history.state?.createdUserObject as User;
      if (userId && stateUser?.id === userId) {
        this.loadDatas(userId, stateUser);
      } else {
        this.loadDatas(userId);
      }
    });

    this.externalParametersChange =
      this.externalsParametersService.externalParametersChange$.subscribe(
        (parameters) => {
          if (parameters) {
            this.isExternal = true;
            this.externalParameter = parameters;
          }
        }
      );
  }

  canModify(): boolean {
    return this.canEdit || this.isCreation;
  }

  getUserProfil(user: User): string | null {
    const profilProp = user?.properties?.find(
      (p) => p.name?.trim() === 'NexusProfil'
    );
    const techName = profilProp?.value?.trim() || '';

    const profilMap: Record<string, string> = {
      NexusCommande: 'Acheteur',
      NexusFacture: 'Gestionnaire financier',
      NexusCentrex: 'Référent technique Centrex',
      NexusMVNO: 'Référent technique MVNO',
    };

    return profilMap[techName] || techName || null;
  }

  ngOnDestroy(): void {
    if (this.externalParametersChange) {
      this.externalParametersChange.unsubscribe();
    }
  }

  /** Lors du changement des droits */
  onRightsChanged(userRights: Acl[]) {
    this.userRights = userRights;
  }

  /** Lors du changement des propriétés */
  onPropertiesChanged(userProperties: Property[]) {
    this.userProperties = userProperties;
  }

  createForm(user: User): void {
    const shortDate = (date: Date | string | undefined | null): string =>
      date ? new Date(date).toLocaleString() : '';

    const generalGroupConfig: { [key: string]: any } = {
      displayOrganization: [
        { value: user.displayParentOrganization, disabled: true },
      ],
      display: [
        {
          value: user.display,
          disabled: this.isCreation
            ? false
            : this.currentUser.id !== this.userInformations.id
            ? false
            : true,
        },
      ],
      account: this.isCreation
        ? [
            user.account,
            [
              Validators.required,
              Validators.pattern(
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
              ),
            ],
          ]
        : [{ value: user.account, disabled: true }, [Validators.required]],
      networks: [
        {
          value: user.networks,
          disabled: this.currentUser.id !== this.userInformations.id,
        },
      ],
      password: [''],
      enabled: [
        { value: user.enabled, disabled: !this.isFieldEnabled('enabled') },
      ],
      billing: [{ value: user.billing, disabled: true }],
      appData: [
        {
          value: user.appData,
          disabled: this.currentUser.id !== this.userInformations.id,
        },
      ],
      createdAt: [{ value: shortDate(user.createdAt), disabled: true }],
      deletedAt: [{ value: user.deletedAt, disabled: true }],
      organizationPath: [{ value: user.organizationPath, disabled: true }],
      lastConnection: [
        { value: shortDate(user.lastConnection), disabled: true },
      ],
      organizationAccessLevel: [
        {
          value: user.organizationAccessLevel,
          disabled: this.currentUser.id !== this.userInformations.id,
        },
      ],
      userAccessLevel: [
        {
          value: user.userAccessLevel,
          disabled: this.currentUser.id !== this.userInformations.id,
        },
      ],
    };

    // Ajout dynamique du champ profilId uniquement si création
    if (this.isCreation) {
      generalGroupConfig['profilId'] = [null];
    }

    this.userForm = this.formBuilder.group({
      generalGroup: this.formBuilder.group(generalGroupConfig),
    });

    this.spinner.hide('spinner');
  }

  private isFieldEnabled(field: keyof User): boolean {
    if (this.isCreation) return true;
    if (this.currentUser.userAccessLevel === 'Full') return true;
    return this.currentUser.id !== this.userInformations.id;
  }

  commitChanges(): void {
    const formValues = this.userForm.get('generalGroup')!.getRawValue();

    this.userInformations.account = formValues.account;
    this.userInformations.display = this.isCreation
      ? formValues.account?.trim()
      : formValues.display?.trim();

    this.userInformations.enabled = formValues.enabled;
    this.userInformations.networks = formValues.networks;
    this.userInformations.organizationAccessLevel =
      formValues.organizationAccessLevel;
    this.userInformations.userAccessLevel = formValues.userAccessLevel;

    if (this.isCreation) {
      this.userInformations.password = formValues.password;
    }
  }

  onSubmit(): void {
    this.spinner.show('spinner');
    this.commitChanges();

    if (this.isCreation) {
      const formValues = this.userForm.get('generalGroup')!.getRawValue();

      // Ajout automatique des propriétés attendues
      const basicProps: Property[] = [
        { name: 'Firstname', value: '', legacy: false },
        { name: 'Lastname', value: '', legacy: false },
        { name: 'Email', value: formValues.account ?? '', legacy: false },
        { name: 'Phone', value: '', legacy: false },
        { name: 'Function', value: '', legacy: false },
        { name: 'Address', value: '', legacy: false },
        { name: 'Zipcode', value: '', legacy: false },
        { name: 'City', value: '', legacy: false },
        { name: 'Country', value: '', legacy: false },
        // { name: 'partenaireId', value: '', legacy: false },
        // { name: 'Clientid', value: '', legacy: false },
      ];

      // Injection des propriétés manquantes
      basicProps.forEach((p) => {
        const exists = this.userProperties.find((up) => up.name === p.name);
        if (!exists) this.userProperties.push(p);
      });

      // Détermination du profil sélectionné
      let selectedProfil;

      if (this.isAdminMode) {
        // ► Mode création ADMIN ⇒ on impose "NexusAdministrateur"
        selectedProfil = {
          id: this.profilIdToApply,
          name: 'NexusAdministrateur',
        };
      } else {
        // ► Mode création USER normal
        selectedProfil = this.profils.find(
          (p) => p.id === this.profilIdToApply
        );
      }

      if (!selectedProfil) {
        this.toastr.error('Aucun profil utilisateur sélectionné.');
        this.spinner.hide('spinner');
        return;
      }

      // Supprime toute ancienne propriété NexusProfil
      this.userProperties = this.userProperties.filter(
        (p) => p.name !== 'NexusProfil'
      );

      // Ajoute la propriété NexusProfil en fonction du profil choisi
      this.userProperties.push({
        name: 'NexusProfil',
        value: selectedProfil.name, // ex: "NexusCentrex", "NexusFacture", etc.
        legacy: false,
      });

      // Associe les propriétés finales à l'utilisateur
      this.userInformations.properties = this.userProperties;

      // Création du user
      this.usersService.addUser(this.userInformations).subscribe({
        next: (newUser) => {
          if (this.profilIdToApply) {
            // Applique le profil de base
            this.profilService
              .applyProfil(newUser.id, this.profilIdToApply)
              .subscribe({
                next: () => {
                  // Puis met à jour les propriétés ajustées par l’admin
                  const updateRequests = this.userProperties
                    .filter(
                      (p) => p.name !== 'partenaireId' && p.name !== 'Clientid'
                    )
                    .map((p) =>
                      this.usersService.updateUserProperty(
                        newUser.id,
                        p.name,
                        p.value
                      )
                    );

                  from(updateRequests)
                    .pipe(concatMap((req) => req)) // exécute les requêtes une par une
                    .subscribe({
                      next: () => {},
                      complete: () => {
                        this.toastr.success(
                          `Utilisateur "${newUser.display}" créé avec succès`
                        );
                        this.router.navigate([
                          this.isAdminMode ? '/admin' : '/users',
                        ]);
                        this.spinner.hide('spinner');
                      },
                      error: (err) => {
                        console.error(
                          'Erreur lors de la mise à jour des propriétés :',
                          err
                        );
                        this.spinner.hide('spinner');
                      },
                    });
                },
                error: () => this.spinner.hide('spinner'),
              });
          } else {
            // Aucun profil à appliquer, on termine directement
            this.toastr.success(
              `Utilisateur "${newUser.display}" créé avec succès`
            );
            this.router.navigate([this.isAdminMode ? '/admin' : '/users']);
            this.spinner.hide('spinner');
          }
        },
        error: () => this.spinner.hide('spinner'),
      });
    }
    // Cas modification d’un utilisateur existant
    else {
      const requests: Observable<any>[] = this.getPropertiesAndRightsRequests(
        this.userInformations.id
      );

      if (this.currentUser.id !== this.userInformations.id) {
        requests.push(this.usersService.updateUser(this.userInformations));
      }

      if (requests.length === 0) {
        this.spinner.hide('spinner');
        this.toastr.info('Aucune modification à enregistrer.');
        return;
      }

      // Exécution séquentielle des mises à jour
      from(requests)
        .pipe(concatMap((req) => req))
        .subscribe({
          complete: () => {
            if (this.isExternal) {
              const response = new ExternalResponse();
              response.code = 200;
              window.location.href = `${this.externalParameter.return}${
                this.externalParameter.return?.includes('?') ? '&' : '?'
              }response=${JSON.stringify(response)}`;
            } else {
              this.toastr.success('Utilisateur mis à jour avec succès');
              this.router.navigate([this.isAdminMode ? '/admin' : '/users']);
            }
            this.spinner.hide('spinner');
          },
          error: (error: unknown) => {
            if (this.isExternal) {
              const httpError = error as HttpErrorResponse;
              const response = new ExternalResponse();
              response.code = httpError.status;
              window.location.href = `${this.externalParameter.return}${
                this.externalParameter.return?.includes('?') ? '&' : '?'
              }response=${JSON.stringify(response)}`;
            } else {
              console.error('Erreur lors de la mise à jour :', error);
              this.toastr.error(
                "Erreur lors de la mise à jour de l'utilisateur."
              );
              this.loadDatas(this.userInformations.id);
            }
            this.spinner.hide('spinner');
          },
        });
    }
  }

  getPropertiesAndRightsRequests(userId: string): any[] {
    const requests: any[] = [];

    this.userProperties.forEach((p) => {
      if (p.name === 'partenaireId' || p.name === 'Clientid') return; // AJOUT OBLIGATOIRE

      const existing = this.userInformations.properties.find(
        (up) => up.name === p.name
      );

      if (existing) {
        if (existing.value !== p.value) {
          requests.push(
            this.usersService.updateUserProperty(userId, p.name, p.value)
          );
        }
      } else {
        requests.push(
          this.usersService.updateUserProperty(userId, p.name, p.value)
        );
      }
    });

    this.userInformations.properties.forEach((p) => {
      if (!this.userProperties.find((up) => up.name === p.name)) {
        requests.push(this.usersService.deleteUserProperty(userId, p.name));
      }
    });

    this.userRights.forEach((r) => {
      const existing = this.userInformations.rights.find(
        (ur) => ur.name === r.name
      );
      if (existing) {
        if (existing.value !== r.value) {
          requests.push(
            this.usersService.updateUserRight(userId, r.name, r.value)
          );
        }
      } else {
        requests.push(
          this.usersService.updateUserRight(userId, r.name, r.value)
        );
      }
    });

    this.userInformations.rights.forEach((r) => {
      if (!this.userRights.find((ur) => ur.name === r.name)) {
        requests.push(this.usersService.deleteUserRight(userId, r.name));
      }
    });

    return requests;
  }

  loadDatas(userId: string, preloadedUser?: User): void {
    this.spinner.show('spinner');

    if (userId) {
      // MODE ÉDITION → inchangé
      const userObs = preloadedUser
        ? of(preloadedUser)
        : this.usersService.getUserWithDisplayParentOrganization(userId);

      forkJoin([userObs, this.usersService.getSessionCurrentUser()])
        .pipe(
          switchMap(([user, currentUser]) => {
            if (!user || !currentUser) {
              this.toastr.error(
                'Impossible de charger les données utilisateur.'
              );
              this.router.navigate([this.isAdminMode ? '/admin' : '/users']);
              return of([]);
            }

            this.userInformations = user;
            this.currentUser = currentUser;
            this.userProperties = [...user.properties];
            this.userRights = [...user.rights];
            this.isCreation = false;

            this.partenaireInput = this.getOrganizationDisplay(
              user.organizationId
            );

            return this.organizationsService.getOrganisations();
          })
        )
        .subscribe({
          next: (organizations) => {
            this.organizationList = organizations;
            this.createForm(this.userInformations);
            this.spinner.hide('spinner');
          },
          error: () => this.spinner.hide('spinner'),
        });

      return;
    }

    // MODE CRÉATION
    this.organizationsService.getOrganisations().subscribe((orgs) => {
      const org = this.organizationsService.getCurrentOrganization() ?? orgs[0];

      if (!org) {
        this.toastr.error('Aucune organisation disponible.');
        this.router.navigate([this.isAdminMode ? '/admin' : '/users']);
        return;
      }

      this.userInformations = new User();

      // Organisation par défaut
      this.userInformations.organizationId = org.id;
      this.userInformations.displayParentOrganization = org.display;

      // Déterminer partenaire vs client
      const isPartner = !org.organizationId;
      const key = isPartner ? 'partenaireId' : 'Clientid';

      // Injecter la propriété GUM
      this.userProperties = this.userProperties.filter((p) => p.name !== key);
      this.userProperties.push({
        name: key,
        value: org.id,
        legacy: false,
      });

      this.clientInput = org.display;
      this.isCreation = true;
      this.organizationList = orgs;

      // Charger profils standard (USER)
      this.loadProfils();

      // MODE ADMIN : charger le profil admin AVANT createForm()
      if (this.isAdminMode) {
        this.profilService.getProfils().subscribe((allProfils) => {
          const adminProfil = allProfils.find(
            (p) => p.name === 'NexusAdministrateur'
          );

          if (adminProfil) {
            this.profilIdToApply = adminProfil.id;

            // Charge les propriétés du profil admin
            this.onProfilSelected(adminProfil.id);
          }

          // Très important : créer le formulaire APRÈS avoir chargé le profil admin
          this.createForm(this.userInformations);
          this.spinner.hide('spinner');
        });

        return; // empêcher l’appel du createForm en double
      }

      // MODE USER simple : création normale
      this.createForm(this.userInformations);
      this.spinner.hide('spinner');
    });
  }

  private loadProfils() {
    this.profilService.getProfils().subscribe((res) => {
      const profilMap: Record<string, string> = {
        NexusCommande: 'Acheteur',
        NexusFacture: 'Gestionnaire financier',
        NexusCentrex: 'Référent technique Centrex',
        NexusMVNO: 'Référent technique MVNO',
      };

      this.profils = res
        .filter((p) => Object.keys(profilMap).includes(p.name))
        .map((p) => ({
          ...p,
          displayName: profilMap[p.name],
        }));

      // ICI : si c’est un admin en création → charger son profil
      if (this.isAdminMode && this.isCreation) {
        const adminProfil = this.profils.find(
          (p) => p.name === 'NexusAdministrateur'
        );
        if (adminProfil) {
          this.profilIdToApply = adminProfil.id;
          this.onProfilSelected(adminProfil.id);
        }
      }
    });
  }

  onProfilSelected(profilId: number): void {
    this.profilIdToApply = profilId;

    this.profilService.getProfil(profilId).subscribe((profil) => {
      this.userProperties = profil.properties.map((p) => {
        const prop = new Property();
        prop.name = p.propertyName;
        prop.value = String(p.propertyValue);
        prop.legacy = false;
        return prop;
      });

      // Aperçu du profil à afficher
      this.selectedProfilProperties = profil.properties.map((p) => ({
        name: p.propertyName,
        value: p.propertyValue,
      }));

      this.propertiesValid = true;
    });
  }

  getPropertyValue(name: string): string | null {
    const source = this.isCreation
      ? this.selectedProfilProperties.map((p) => ({
          name: p.name,
          value: String(p.value),
        }))
      : this.userProperties;
    const found = source.find((p) => p.name === name);
    return found?.value ?? null;
  }

  getNumericPropertyValue(name: string): number | null {
    const val = this.getPropertyValue(name);
    const num = Number(val);
    return isNaN(num) ? null : num;
  }

  getOptionsForProperty(propName: string): { value: number; label: string }[] {
    // Propriétés avec 3 niveaux de droits
    const withFullAccess = ['NexusCentrex', 'NexusMVNO'];

    if (withFullAccess.includes(propName)) {
      return [
        { value: 0, label: 'Accès interdit' },
        { value: 1, label: 'Accès lecture seule' },
        { value: 2, label: 'Accès complet' },
      ];
    } else {
      return [
        { value: 0, label: 'Accès non autorisé' },
        { value: 1, label: 'Accès autorisé' },
      ];
    }
  }

  onPropertyChanged(name: string, value: any): void {
    if (name === 'partenaireId') {
      this.clientsList = this.getDescendantOrganizations(value);
      this.partenaireInput = this.getOrganizationDisplay(value);

      // Réinitialiser clientId
      const clientProp = this.userProperties.find((p) => p.name === 'Clientid');
      if (clientProp) clientProp.value = '';

      // ne pas ajouter de propriété "partenaireId"
      this.userInformations.organizationId = value;
      return; // on sort, pas de création de propriété
    }

    if (name === 'Clientid') {
      this.clientInput = this.getOrganizationDisplay(value);
    }

    const existing = this.userProperties.find((p) => p.name === name);
    if (existing) {
      existing.value = String(value);
    } else {
      const prop = new Property();
      prop.name = name;
      prop.value = String(value);
      this.userProperties.push(prop);
    }
  }

  private getDescendantOrganizations(parentId: string): Organization[] {
    const descendants: Organization[] = [];

    const collect = (parent: string) => {
      const children = this.organizationList.filter(
        (org) => org.organizationId === parent
      );
      for (const child of children) {
        descendants.push(child);
        collect(child.id); // appel récursif
      }
    };

    collect(parentId);
    return descendants;
  }

  onTextInput(name: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.onPropertyChanged(name, value);
  }

  trackByPropertyName(index: number, item: Property): string {
    return item.name;
  }

  getOrganizationDisplay(id: string | number | null): string {
    if (!id) return '';
    const all = [...(this.organizationList || []), ...(this.clientsList || [])];
    const found = all.find((o) => o.id?.toString() === id?.toString());
    return found?.display || '';
  }

  onPartenaireInput(event: Event): void {
    this.partenaireInput = (event.target as HTMLInputElement).value;
  }

  onClientInput(event: Event): void {
    this.clientInput = (event.target as HTMLInputElement).value;
  }

  cancelButtonClick(): void {
    if (this.isExternal) {
      const returnUrl = this.externalParameter?.return ?? '/';
      window.location.href = returnUrl;
    } else {
      this.router.navigate([this.isAdminMode ? '/admin' : '/users']);
    }
  }
}
